/**
 * Which product each top-level route belongs to, and what that means for
 * where an organization may go (#11422 round 17). The product list is the
 * one the sign-in answer carries (`organization.allowed_products`); the
 * frontend never learns which client an organization is.
 */

/**
 * Top-level routes, each with the product that owns it, in landing order:
 * "/" goes to the first one the organization has. The claims queue leads, so
 * a claims-only organization always lands there; chat comes next, as it
 * always has for everyone else. Artifacts is last because every organization
 * on a full deployment carries it, so it says nothing about what the
 * organization came for.
 */
const PRODUCT_ROUTES: ReadonlyArray<{ path: string; product: string }> = [
  { path: "/claims", product: "bcbswy_intake" },
  { path: "/chat", product: "chat" },
  { path: "/rms", product: "crm" },
  { path: "/documents", product: "manage_documents" },
  { path: "/matrix", product: "querygrid" },
  { path: "/reports", product: "report_generation" },
  { path: "/notetaker", product: "notetaker" },
  { path: "/alerts", product: "alerts" },
  { path: "/exposures", product: "exposures" },
  { path: "/tasks", product: "tasks_full" },
  { path: "/agents", product: "agents" },
  { path: "/artifacts", product: "artifacts" },
];

const SETTINGS_PATH = "/settings";
const CLAIMS_PATH = "/claims";

const under = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * An organization with the claims product and no chat. Its claim files are
 * kept out of the filesystem, so the rest of the product has nothing of its
 * work, and on the document-intake deployment most of it is not registered.
 */
export const isClaimsOnly = (products: readonly string[]): boolean =>
  products.includes("bcbswy_intake") && !products.includes("chat");

/**
 * Where "/" lands: the first route in landing order whose product the
 * organization has (the claims queue for a claims-only organization, chat
 * for one that has chat), and Settings when it has none.
 */
export const homePath = (products: readonly string[]): string =>
  PRODUCT_ROUTES.find((r) => products.includes(r.product))?.path ??
  SETTINGS_PATH;

/**
 * Where a claims-only organization is sent from `pathname`, or null when it
 * may stay. It keeps Settings and the routes of the products it has; every
 * other route goes to the claims queue. Any other organization is never sent
 * anywhere.
 */
export const claimsOnlyRedirect = (
  pathname: string,
  products: readonly string[]
): string | null => {
  if (!isClaimsOnly(products)) return null;
  if (under(pathname, SETTINGS_PATH)) return null;
  const owned = PRODUCT_ROUTES.some(
    (r) => under(pathname, r.path) && products.includes(r.product)
  );
  return owned ? null : CLAIMS_PATH;
};
